/**
 * @fileoverview Static reference data for the banking feature — fiscal-year window, branch
 * network, region grouping, and the bank's static profile fields.
 * @description Ported from the InsightBox Banking design system's own `data/bank.ts`. Not fetched
 * from an endpoint — enumerated domain values, same convention as `sales-options.constants.ts`.
 * Region-level scoping (`regionId`/`isRegionId`/`locationForRegionId`/`branchesInRegion`) backs the
 * Bank Regional Manager role's data lock (see `features/banking/hooks/useBankingAccessScope.ts`).
 * The reference's own demo `ROLES` catalog and notification list/app-version are still dropped —
 * this app resolves a locked role's scope from the signed-in user's session
 * (`CurrentPrincipal.bankingScope`) instead of a client-side role-switcher, and notifications/app
 * version aren't banking-specific concerns here.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

export const BANK_NAME = 'HSH Co-operative Bank Ltd.'

export const FISCAL_YEARS = ['FY21', 'FY22', 'FY23', 'FY24', 'FY25'] as const
export type FiscalYear = (typeof FISCAL_YEARS)[number]

export const LATEST_FY: FiscalYear = 'FY25'

// The Year filter has 6 states: the consolidated "5 Years" view (every dashboard's default) plus
// each individual FY. Picking an individual year narrows a page down to that year alone; "5
// Years" shows the full multi-year trend/consolidated view. Pages with no multi-year concept of
// their own (composition/mix breakdowns) just resolve "5 Years" to the latest year via resolveFy.
export const FIVE_YEAR_ID = '5years'
export type FyFilter = FiscalYear | typeof FIVE_YEAR_ID

/**
 * @description Resolves a Year filter value down to a concrete fiscal year.
 * @param {FyFilter} fy - The filter's current value.
 * @returns {FiscalYear} `fy` itself, or `LATEST_FY` when `fy` is the "5 Years" id.
 */
export function resolveFy(fy: FyFilter): FiscalYear {
  return fy === FIVE_YEAR_ID ? LATEST_FY : fy
}

/**
 * @description The fiscal year's position in `FISCAL_YEARS`.
 * @param {FiscalYear} fy - The fiscal year to look up.
 * @returns {number} Its zero-based index.
 */
export function fyIndex(fy: FiscalYear): number {
  return FISCAL_YEARS.indexOf(fy)
}

/**
 * @description The fiscal year immediately before `fy`.
 * @param {FiscalYear} fy - The fiscal year to look before.
 * @returns {FiscalYear | null} The prior fiscal year, or `null` if `fy` is the earliest one.
 */
export function prevFY(fy: FiscalYear): FiscalYear | null {
  const index = fyIndex(fy)
  return index > 0 ? (FISCAL_YEARS[index - 1] ?? null) : null
}

/**
 * @description The bank's own Apr–Mar year-range notation for a fiscal year — e.g. `FY25` (ending
 * 31 Mar 2025) reads as "FY 24-25". Ported from the InsightBox Banking design system's own
 * `PageFilterBar.tsx`.
 * @param {FiscalYear} fy - The fiscal year to format.
 * @returns {string} The "FY YY-YY" range label.
 */
export function fyRangeLabel(fy: FiscalYear): string {
  const end = Number.parseInt(fy.slice(2), 10)
  const start = end - 1
  return `FY ${String(start).padStart(2, '0')}-${String(end).padStart(2, '0')}`
}

// Indian FY calendar order (Apr -> Mar), used for within-year monthly drill-down views.
export const FY_MONTHS = [
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
  'Jan',
  'Feb',
  'Mar',
] as const

export interface Branch {
  id: string
  name: string
  shortName: string
  location: 'Pune' | 'Pimpri-Chinchwad'
}

export const BRANCHES: Branch[] = [
  { id: 'ho', name: 'Head Office Branch', shortName: 'HO', location: 'Pune' },
  { id: 'shivajinagar', name: 'Shivajinagar Branch', shortName: 'Shivajinagar', location: 'Pune' },
  { id: 'kothrud', name: 'Kothrud Branch', shortName: 'Kothrud', location: 'Pune' },
  { id: 'hadapsar', name: 'Hadapsar Branch', shortName: 'Hadapsar', location: 'Pune' },
  { id: 'baner', name: 'Baner Branch', shortName: 'Baner', location: 'Pune' },
  { id: 'pimpri', name: 'Pimpri Branch', shortName: 'Pimpri', location: 'Pimpri-Chinchwad' },
  {
    id: 'chinchwad',
    name: 'Chinchwad Branch',
    shortName: 'Chinchwad',
    location: 'Pimpri-Chinchwad',
  },
  { id: 'wakad', name: 'Wakad Branch', shortName: 'Wakad', location: 'Pune' },
  { id: 'aundh', name: 'Aundh Branch', shortName: 'Aundh', location: 'Pune' },
  { id: 'camp', name: 'Camp Branch', shortName: 'Camp', location: 'Pune' },
  { id: 'sinhagad-road', name: 'Sinhagad Road Branch', shortName: 'Sinhagad Rd', location: 'Pune' },
  { id: 'yerawada', name: 'Yerawada Branch', shortName: 'Yerawada', location: 'Pune' },
]

export const ALL_BRANCHES_ID = 'all'

const REGION_ID_PREFIX = 'region-'
const REGION_LOCATIONS: Branch['location'][] = ['Pune', 'Pimpri-Chinchwad']

/**
 * @description The pseudo branch-id representing "every branch in this region" — the Regional
 * Manager equivalent of `ALL_BRANCHES_ID`, but scoped to one location instead of the whole network.
 * @param {Branch['location']} location - The region's location name.
 * @returns {string} The region's pseudo id, e.g. `"region-pune"`.
 */
export function regionId(location: Branch['location']): string {
  return `${REGION_ID_PREFIX}${location.toLowerCase().replace(/[^a-z]+/g, '-')}`
}

/**
 * @description Whether a branch-filter value is a region pseudo-id rather than `ALL_BRANCHES_ID` or
 * a real branch id.
 * @param {string} id - The value to check.
 * @returns {boolean} True if `id` was produced by `regionId`.
 */
export function isRegionId(id: string): boolean {
  return id.startsWith(REGION_ID_PREFIX)
}

/**
 * @description The reverse of `regionId` — recovers which location a region pseudo-id refers to.
 * @param {string} id - A region pseudo-id (as produced by `regionId`).
 * @returns {Branch['location'] | null} The matching location, or `null` if `id` isn't a region id.
 */
export function locationForRegionId(id: string): Branch['location'] | null {
  return REGION_LOCATIONS.find((location) => regionId(location) === id) ?? null
}

/**
 * @description Every real branch within a region.
 * @param {Branch['location']} location - The region's location name.
 * @returns {Branch[]} The branches whose `location` matches.
 */
export function branchesInRegion(location: Branch['location']): Branch[] {
  return BRANCHES.filter((branch) => branch.location === location)
}

export const BANK_DETAILS = {
  basicInfo: [
    { label: 'Bank Name', value: BANK_NAME },
    { label: 'Bank Type', value: 'Urban Co-operative Bank (Multi-State Scheduled)' },
    { label: 'Registration Number', value: 'MSCS/CR/1187/1998' },
    { label: 'Head Office Location', value: 'Pune, Maharashtra' },
    { label: 'Website', value: 'www.hshbank.co.in' },
  ],
  address: [
    { label: 'Street Address', value: '402, Shivajinagar Main Road' },
    { label: 'City', value: 'Pune' },
    { label: 'District', value: 'Pune' },
    { label: 'State', value: 'Maharashtra' },
    { label: 'Pincode', value: '411005' },
  ],
  organization: [
    { label: 'No. of Branches', value: '12' },
    { label: 'No. of Employees', value: '190' },
    { label: 'Bank Tier', value: 'Tier 2 (RBI UCB Classification)' },
    { label: 'Core Banking Solution', value: 'BR.Net CBS' },
    { label: 'Existing MIS Solution', value: 'No' },
  ],
}
